// Shared period-comparison presets — used by GoogleAdsCompareTab and
// GoogleAdsCrossPracticeTab so the two tabs stay in lockstep.
//
// "Today" is excluded from every "Last N" range. Google Ads' own UI follows
// the same convention because today's data hasn't been finalised yet, and
// having a partial day at the right edge skews period-over-period comparisons.
// Period B is auto-paired to the equivalent-length window immediately
// preceding Period A.

import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

// Without this, dayjs treats 'quarter' as an unrecognised unit and silently
// no-ops on .startOf('quarter') / .add(n, 'quarter') / .subtract(n, 'quarter')
// — the 'thisQuarter' preset below then collapses Period A to "today" and
// produces an inverted (start > end) Period B, so every metric (Spend
// included) reads as zero / broken. MetaAdsPeriodCompareTab.jsx already
// extends this plugin, but only for whichever page happens to import it
// first — dayjs plugins are global, so relying on import order elsewhere is
// fragile. Extend it here, at the shared util's own module scope, so every
// consumer (GoogleAdsCompareTab, GoogleAdsCrossPracticeTab) gets a correct
// 'quarter' unit regardless of what else has loaded.
dayjs.extend(quarterOfYear);

// Natural "percent change from B → A" for the period-comparison views.
// Returns null when the previous period (B) was empty and the current (A)
// wasn't — UI renders null as "—" rather than ∞ / NaN.
//
// We compute this client-side instead of trusting backend's `change` field
// on both /daily-metrics/compare/ and /cross-practice-compare/ — those use
// pct_change(a, b) = (b - a) / a, which flips the sign AND uses the wrong
// denominator for the user-natural "% change from previous period" reading.
export function computeDelta(a, b) {
    const av = Number(a || 0);
    const bv = Number(b || 0);
    if (bv === 0) return av === 0 ? 0 : null;
    return ((av - bv) / bv) * 100;
}

export const PRESET_DEFS = [
    { key: 'last7', label: 'Last 7 vs Previous 7' },
    { key: 'last30', label: 'Last 30 vs Previous 30' },
    { key: 'thisMonth', label: 'This Month vs Last Month' },
    { key: 'lastMonth', label: 'Last Month vs Month Before' },
    { key: 'thisQuarter', label: 'This Quarter vs Last' },
    { key: 'custom', label: 'Custom' },
];

export function buildPreset(key, now = dayjs()) {
    const today = now;
    const yesterday = today.subtract(1, 'day').endOf('day');
    switch (key) {
        case 'last7': {
            const aStart = today.subtract(7, 'day').startOf('day');
            const aEnd = yesterday;
            const bStart = aStart.subtract(7, 'day');
            const bEnd = aStart.subtract(1, 'day').endOf('day');
            return { aStart, aEnd, bStart, bEnd };
        }
        case 'last30': {
            const aStart = today.subtract(30, 'day').startOf('day');
            const aEnd = yesterday;
            const bStart = aStart.subtract(30, 'day');
            const bEnd = aStart.subtract(1, 'day').endOf('day');
            return { aStart, aEnd, bStart, bEnd };
        }
        case 'thisMonth': {
            const aStart = today.startOf('month');
            // Guard the edge case where today is the 1st — yesterday is in
            // last month, which would give aEnd < aStart. Fall back to aStart.
            const aEnd = yesterday.isBefore(aStart) ? aStart : yesterday;
            const bStart = aStart.subtract(1, 'month');
            const bEnd = aStart.subtract(1, 'day').endOf('day');
            return { aStart, aEnd, bStart, bEnd };
        }
        case 'lastMonth': {
            const aStart = today.subtract(1, 'month').startOf('month');
            const aEnd = today.subtract(1, 'month').endOf('month');
            const bStart = aStart.subtract(1, 'month');
            const bEnd = aStart.subtract(1, 'day').endOf('day');
            return { aStart, aEnd, bStart, bEnd };
        }
        case 'thisQuarter': {
            // "Quarter to date" — from the first day of the current calendar
            // quarter through yesterday — vs. the same elapsed window in the
            // previous quarter. This is the apples-to-apples comparison the
            // label promises (the old impl was a trailing-90-day window
            // dressed up as a quarter — misleading on quarter boundaries).
            const aStart = today.startOf('quarter');
            const aEnd = yesterday.isBefore(aStart) ? aStart : yesterday;
            // Inclusive day-count of Period A, so Period B mirrors its length.
            // Deriving this from (aEnd - aStart) rather than (today - aStart)
            // matters right on a quarter boundary (e.g. today is the 1st of
            // the quarter, so aStart === aEnd): the old "elapsed days from
            // today" gave 0, which made bEnd = bStart - 1 day — an inverted
            // (start-after-end) Period B. Using the actual A-window length
            // (minimum 1 day) keeps B a valid, non-inverted range.
            const periodLengthDays = aEnd.diff(aStart, 'day') + 1;
            const bStart = aStart.subtract(1, 'quarter');
            const bEnd = bStart.add(periodLengthDays - 1, 'day').endOf('day');
            return { aStart, aEnd, bStart, bEnd };
        }
        default:
            return null;
    }
}

// ---------------------------------------------------------------------------
// Date-range validation for user-typed custom ranges.
//
// A keyboard-typed DD/MM/YYYY field emits intermediate values on every
// keystroke ("Invalid Date", year 0920, year 9202 ...). None of those may ever
// reach an API query string, so everything that builds a date-keyed request
// goes through these helpers.
export const MIN_VALID_YEAR = 2000;
export const MAX_VALID_YEAR = 2100;

export function isUsableDate(d) {
    if (!d || typeof d.isValid !== 'function' || !d.isValid()) return false;
    const y = d.year();
    return y >= MIN_VALID_YEAR && y <= MAX_VALID_YEAR;
}

// Returns a human-readable error string, or '' when all four dates form two
// valid periods (each start <= end).
export function getCustomRangeError(aStart, aEnd, bStart, bEnd) {
    const all = [aStart, aEnd, bStart, bEnd];
    if (all.some((d) => !d)) return 'Enter all four dates.';
    if (!all.every(isUsableDate)) {
        return `Enter complete dates (DD/MM/YYYY) between ${MIN_VALID_YEAR} and ${MAX_VALID_YEAR}.`;
    }
    if (aStart.isAfter(aEnd, 'day')) return 'Period A start must be on or before its end.';
    if (bStart.isAfter(bEnd, 'day')) return 'Period B start must be on or before its end.';
    return '';
}

// Formats a usable date as YYYY-MM-DD, or '' for anything partial/invalid.
export function formatApiDate(d) {
    return isUsableDate(d) ? d.format('YYYY-MM-DD') : '';
}
